import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { listen, abortListen } from "Services/Listen";
import { COPY_STRINGS } from "Constants/translatedCopy";
import "./Morpheme.scss";
import { sleep, shuffle } from "../shared/util";
import Word, { Carousel } from "../shared/Word";
import ListeningStatus from "Components/Global/ListeningStatus";
import useConversation from "../shared/useConversation";
import Container from "../shared/Container";
import { getDistractors } from "./distractor";
import icon from "./morpheme-icon.png";
import {getUserData} from "Services/API";
import useIsMounted from "../shared/hooks/useIsMounted";
import useCancellablePromise from "../shared/hooks/useCancellablePromise";

let ttsStrings;

export const morphemePositions = {
  prefix: "prefix",
  suffix: "suffix",
  root: "root",
};

export default function MorphemeValidation(props) {
  ttsStrings = COPY_STRINGS.intervention_strings.morpheme;

  const [isValid, setIsValid] = useState(false);

  const isMountedRef = useIsMounted();

  useEffect(() => {
    const m = props.wordMetadata;
    if (
      m.MORPHEME &&
      m.MORPHEME_POSITION &&
      m.MORPHEME_MEANING &&
      m.MORPHEME_ALTS
    )
      setIsValid(true);
    else return close();
  }, []);

  if (!isValid) return null;
  return <Morpheme {...props} close={close} stopListen={stopListen} isMountedRef={isMountedRef} />;

  function close() {
    if (!isMountedRef.current) return;

    props.handleMessage({
      continueWithoutSpeaking: true,
      endOfPhrase: props.endOfPhrase,
    });
  }

  function stopListen() {
    abortListen(props.speech, props.endOfPhrase);
  }
}

function Morpheme(props) {
  const { word, wordMetadata, speech } = props;
  const { MORPHEME, MORPHEME_POSITION, MORPHEME_MEANING, MORPHEME_ALTS } =
    wordMetadata;
  const morphemePosition = ttsStrings[MORPHEME_POSITION];

  const { speak, listenWord, speakSequence } = useConversation(speech, listen);
  const { cancellablePromise } = useCancellablePromise();

  const [stage, setStage] = useState(0);
  const [revealWord, setRevealWord] = useState(false);
  const [canClick, setCanClick] = useState(false);

  const multipleChoiceData = useMemo(generateChoices, []);

  function generateChoices() {
    const distractors = getDistractors(MORPHEME_POSITION, MORPHEME);
    const options = shuffle([...distractors, word]);
    return { options, target: word };
  }

  // handle stages
  useEffect(() => {
    onStageChange();
  }, [stage]);

  async function onStageChange() {
    if (!props.isMountedRef.current) return;

    try {
      switch (stage) {
        case 0:
          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.intro({morphemePosition}),
              {textToSpeak: MORPHEME, skipTranslation: true},
              ttsStrings.means,
              {textToSpeak: MORPHEME_MEANING, skipTranslation: true},
            ]));
          } else {
            await cancellablePromise(speak(`${ttsStrings.intro({morphemePosition})} ${MORPHEME}, ${ttsStrings.means} ${MORPHEME_MEANING}.`));
          }
          await cancellablePromise(sleep());
          setStage(1);
          break;
        case 1:
          if(getUserData().bilingualMode) {
            await cancellablePromise(speakSequence([
              ttsStrings.examples({morphemePosition}),
              {textToSpeak: MORPHEME, skipTranslation: true},
            ]));
          } else {
            await cancellablePromise(speak(`${ttsStrings.examples({morphemePosition})}, ${MORPHEME}.`));
          }
          break;
        case 2: {
          await cancellablePromise(speak(ttsStrings.ask_word({ morphemePosition })));
          setCanClick(true);
          const { status } = await cancellablePromise(listenWord({
            targetWord: word,
            patterOverride: ["match", "struggle"],
          }));
          if (status === "skip") return props.close();
          setRevealWord(true);
          if (status === "match") await cancellablePromise(onCorrect());
          if (status === "struggle") await cancellablePromise(onStruggle());
          props.close();
          break;
        }
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  return (
    <Container icon={icon}>
      <div style={{ marginBottom: 30 }}>{renderContent()}</div>
      <ListeningStatus />
    </Container>
  );

  function renderContent() {
    if (stage === 0) return <Word>{MORPHEME}</Word>;

    if (stage === 1) {
      return (
        <Carousel
          words={MORPHEME_ALTS}
          underline={MORPHEME}
          onDone={() => {
            setStage(2);
          }}
        />
      );
    }

    if (stage === 2) {
      const { options, target } = multipleChoiceData;
      const wordStyle = { fontSize: 54, margin: "0 30px" };
      const totalLength = options.join("").length;
      if (totalLength > 20) {
        wordStyle.fontSize = 48;
        wordStyle.margin = "0 15px";
      }
      return (
        <div>
          {options.map((word) => {
            let underline;
            if (revealWord && word !== target) wordStyle.color = "gray";
            if (revealWord && word === target) underline = MORPHEME;
            if (canClick) wordStyle.cursor = "pointer";
            return (
              <Word
                key={word}
                style={wordStyle}
                underline={underline}
                canClick={canClick}
                onClick={async () => {
                  if (!canClick) return;
                  props.stopListen();
                  setCanClick(false);
                  setRevealWord(true);
                  const isCorrect = word === target;
                  if (isCorrect) await onCorrect();
                  else await onStruggle();
                  props.close();
                }}
              >
                {word}
              </Word>
            );
          })}
        </div>
      );
    }
  }

  async function onCorrect() {
    if (!props.isMountedRef.current) return;

    try {
      if(getUserData().bilingualMode) {
        await cancellablePromise(speakSequence([
          {textToSpeak: word, skipTranslation: true},
          ttsStrings.correct,
          {textToSpeak: MORPHEME_MEANING, skipTranslation: true},
        ]));
      } else {
        await cancellablePromise(speak(`${word} ${ttsStrings.correct} ${MORPHEME_MEANING}.`));
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }

  async function onStruggle() {
    if (!props.isMountedRef.current) return;

    try {
      const { target } = multipleChoiceData;
      if(getUserData().bilingualMode) {
        await cancellablePromise(speakSequence([
          {textToSpeak: word, skipTranslation: true},
          ttsStrings.struggle({morphemePosition}),
          {textToSpeak: MORPHEME, skipTranslation: true},
        ]));
      } else {
        await cancellablePromise(speak(`${word} ${ttsStrings.struggle({morphemePosition})}, ${MORPHEME}.`));
      }
    } catch (error) {
      if (error.isCancelled) return;
      throw(error);
    }
  }
}

Morpheme.propTypes = {
  word: PropTypes.string,
  wordMetadata: PropTypes.object,
  speech: PropTypes.object,
  handleMessage: PropTypes.func,
  endOfPhrase: PropTypes.bool,
};
